/*
 * assets/hair-advisor.js
 * ------------------------------------------------------------------
 * AEVIA Hair Advisor 2.0 (approved spec "HAIR ADVISOR 2.0" + approved
 * decisions "DECISIONI APPROVATE — IMPLEMENTA HAIR ADVISOR 2.0").
 *
 * Architecture (unchanged): sections/hair-advisor.liquid pre-renders
 * the REAL, frozen snippets/product-card.liquid for every product in
 * collections.all (cap 250) into a hidden pool, each wrapped in a div
 * with a stable data-aevia-advisor-product-id. This file:
 *   - NEVER creates or duplicates Product Card markup (no innerHTML,
 *     no AJAX, no fetch)
 *   - NEVER reads or writes price, image, availability or URL
 *   - only scores the separately embedded JSON dataset and
 *     shows/hides/moves the pre-existing pool nodes
 *   - writes only plain text (textContent) into the existing
 *     data-aevia-match-note paragraph ("why recommended")
 * State is in-memory only: no localStorage, no cookies, no URL state.
 *
 * Answers:
 *   Q1 tipo_capello   single (lisci / mossi / ricci / molto-ricci)
 *   Q2 problema       max 2, ordered by selection (index 0 = primary;
 *                     deselecting the primary promotes the secondary)
 *   Q3 obiettivo      unlimited (soft, non-blocking nudge above 5)
 *   Q4 esigenza       single
 *   Q5 porosita       yes/no
 *   Q6 calore         yes/no
 *   Q7 definizione_ricci yes/no, only when Q1 is ricci / molto-ricci
 *
 * Scoring (approved, deterministic):
 *   score = primary_problem_match   * 3
 *         + secondary_problem_match * 1.5
 *         + 2 * (objective_overlap / sqrt(objective_selected))
 *         + type_match      * 1   (additive only, never excludes)
 *         + need_match      * 2   (exact aevia.esigenza value only)
 *         + porosity        * 1   (Q5 yes AND aevia.problema has "porosita")
 *         + thermal         * 1   (Q6 yes AND aevia.esigenza has "protezione-termica")
 *         + curl_definition * 1   (Q7 yes, Q7 applicable, AND
 *                                  aevia.obiettivo has "definizione")
 *   Tie-break: sortByScoreThenId (numeric Shopify product ID, ascending).
 *
 * Routine: real aevia.routine_step values are mapped here only (the
 * stored values are never changed):
 *   Detersione → CLEANSE | Trattamento → TREAT | Finish → FINISH
 *   Styling/Finish → STYLE
 * Nothing maps to PROTECT or BOOST (no real product has such a value).
 * Max 1 product per step, max 6 products, steps without a candidate
 * are omitted, candidates must have score > 0.
 *
 * Fallback:
 *   Level 1 — partial routine (above), if at least one step is filled.
 *   Level 2 — flat need-only list: products whose aevia.esigenza
 *     contains the Q4 answer, score > 0, sorted, max 8.
 *   Level 3 — honest "no products" message.
 * Never random, never best-seller, never vendor, never score 0.
 */
(function () {
  "use strict";

  var root = document.querySelector("[data-aevia-hair-advisor]");
  if (!root) return;

  var form = root.querySelector("[data-aevia-form]");
  var allSteps = Array.prototype.slice.call(root.querySelectorAll("[data-aevia-step]"));
  var progressEl = root.querySelector("[data-aevia-progress]");
  var backBtn = root.querySelector("[data-aevia-back]");
  var nextBtn = root.querySelector("[data-aevia-next]");
  var resultsEl = root.querySelector("[data-aevia-results]");
  var routineStepEls = Array.prototype.slice.call(root.querySelectorAll("[data-aevia-routine-step]"));
  var fallbackEl = root.querySelector("[data-aevia-fallback]");
  var fallbackProductsEl = root.querySelector("[data-aevia-fallback-products]");
  var noResultsEl = root.querySelector("[data-aevia-no-results]");
  var restartBtn = root.querySelector("[data-aevia-restart]");
  var poolEl = root.querySelector("[data-aevia-pool]");
  var datasetEl = root.querySelector("script[data-aevia-advisor-dataset]");

  if (!form || !allSteps.length || !poolEl || !datasetEl) return;

  var dataset = [];
  try {
    dataset = JSON.parse(datasetEl.textContent) || [];
  } catch (e) {
    dataset = [];
  }

  var REAL_STEPS = ["CLEANSE", "TREAT", "PROTECT", "FINISH", "STYLE", "BOOST"];

  /* Approved mapping of the real Italian aevia.routine_step values. */
  var ROUTINE_STEP_MAP = {
    "Detersione": "CLEANSE",
    "Trattamento": "TREAT",
    "Finish": "FINISH",
    "Styling/Finish": "STYLE"
  };

  var CURL_TYPES = ["ricci", "molto-ricci"];
  var FALLBACK_MAX = 8;
  var OBJECTIVE_NUDGE_ABOVE = 5;

  function emptyAnswers() {
    return {
      tipo_capello: null,
      problema: [], // ordered by selection: [primary, secondary]
      obiettivo: [],
      esigenza: null,
      porosita: null,
      calore: null,
      definizione_ricci: null
    };
  }

  var answers = emptyAnswers();
  var currentIndex = 0;

  /* ---------------------------------------------------------------
   * Step visibility / navigation
   * ------------------------------------------------------------- */

  function answerAsList(value) {
    if (Array.isArray(value)) return value;
    if (value === null || value === undefined || value === "") return [];
    return [value];
  }

  function getVisibleSteps() {
    return allSteps.filter(function (stepEl) {
      var condOn = stepEl.getAttribute("data-aevia-conditional-on");
      if (!condOn) return true;
      var condValues = (stepEl.getAttribute("data-aevia-conditional-values") || "").split(",");
      var current = answerAsList(answers[condOn]);
      return condValues.some(function (v) {
        return current.indexOf(v) !== -1; // exact element match, not substring
      });
    });
  }

  function isCurlQuestionApplicable() {
    return CURL_TYPES.indexOf(answers.tipo_capello) !== -1;
  }

  function stepKey(stepEl) {
    return stepEl.getAttribute("data-aevia-step-key");
  }

  function showError(stepEl, show) {
    var key = stepKey(stepEl);
    var errEl = stepEl.querySelector('[data-aevia-error="' + key + '"]');
    if (errEl) errEl.hidden = !show;
  }

  function updateProgress(index, total) {
    if (progressEl) progressEl.textContent = "Domanda " + (index + 1) + " di " + total;
  }

  function updateNav(index, total) {
    if (backBtn) {
      backBtn.disabled = index === 0;
      backBtn.setAttribute("aria-disabled", backBtn.disabled ? "true" : "false");
    }
    if (nextBtn) {
      nextBtn.textContent = index === total - 1 ? "Vedi il percorso" : "Avanti";
    }
  }

  function showStep(index) {
    var visible = getVisibleSteps();
    if (index >= visible.length) index = visible.length - 1;
    if (index < 0) index = 0;
    currentIndex = index;

    allSteps.forEach(function (s) {
      s.hidden = true;
    });
    var target = visible[currentIndex];
    if (!target) return;
    target.hidden = false;

    updateProgress(currentIndex, visible.length);
    updateNav(currentIndex, visible.length);

    var legend = target.querySelector("legend");
    if (legend) {
      legend.setAttribute("tabindex", "-1");
      legend.focus();
    }
  }

  function isStepAnswered(stepEl) {
    var val = answers[stepKey(stepEl)];
    if (Array.isArray(val)) return val.length > 0;
    return val !== null && val !== undefined && val !== "";
  }

  /* ---------------------------------------------------------------
   * Answer capture
   * ------------------------------------------------------------- */

  function groupInputs(groupName) {
    return Array.prototype.slice.call(form.querySelectorAll('[data-aevia-group="' + groupName + '"]'));
  }

  /* Checkbox groups keep the user's selection ORDER (Q2 priority). */
  function syncCheckboxOrder(groupName, input) {
    var list = answers[groupName].slice();
    var pos = list.indexOf(input.value);
    if (input.checked && pos === -1) list.push(input.value);
    if (!input.checked && pos !== -1) list.splice(pos, 1);
    // keep only values still checked (defensive, e.g. after form.reset())
    var checkedValues = groupInputs(groupName)
      .filter(function (i) {
        return i.checked;
      })
      .map(function (i) {
        return i.value;
      });
    answers[groupName] = list.filter(function (v) {
      return checkedValues.indexOf(v) !== -1;
    });
  }

  function syncRadio(groupName) {
    var checked = groupInputs(groupName).filter(function (i) {
      return i.checked;
    })[0];
    answers[groupName] = checked ? checked.value : null;
  }

  function enforceCap(groupName) {
    var inputs = groupInputs(groupName);
    if (!inputs.length) return;
    var maxAttr = inputs[0].getAttribute("data-aevia-max");
    if (!maxAttr) return;
    var max = parseInt(maxAttr, 10);
    var atCap = answers[groupName].length >= max;

    inputs.forEach(function (i) {
      if (!i.checked) i.disabled = atCap;
    });

    var capMessage = form.querySelector('[data-aevia-cap-message="' + groupName + '"]');
    if (capMessage) capMessage.hidden = !atCap;
  }

  function updateNudge(groupName) {
    var nudge = form.querySelector('[data-aevia-nudge="' + groupName + '"]');
    if (nudge) nudge.hidden = answers[groupName].length <= OBJECTIVE_NUDGE_ABOVE;
  }

  form.addEventListener("change", function (evt) {
    var input = evt.target;
    if (!input.matches("[data-aevia-input]")) return;
    var group = input.getAttribute("data-aevia-group");
    if (input.type === "checkbox") {
      syncCheckboxOrder(group, input);
      enforceCap(group);
      updateNudge(group);
    } else {
      syncRadio(group);
    }
    var stepEl = input.closest("[data-aevia-step]");
    if (stepEl) showError(stepEl, false);
  });

  if (backBtn) {
    backBtn.addEventListener("click", function () {
      if (currentIndex === 0) return;
      showStep(currentIndex - 1);
    });
  }

  if (nextBtn) {
    nextBtn.addEventListener("click", function () {
      var visible = getVisibleSteps();
      var stepEl = visible[currentIndex];
      if (!stepEl) return;
      if (!isStepAnswered(stepEl)) {
        showError(stepEl, true);
        return;
      }
      if (currentIndex === visible.length - 1) {
        submitAdvisor();
        return;
      }
      showStep(currentIndex + 1);
    });
  }

  /* ---------------------------------------------------------------
   * Scoring
   * ------------------------------------------------------------- */

  function has(list, value) {
    return Array.isArray(list) && value !== null && value !== undefined && list.indexOf(value) !== -1;
  }

  function scoreProduct(product) {
    var primary = answers.problema[0] || null;
    var secondary = answers.problema[1] || null;

    var primaryMatch = has(product.problema, primary) ? 1 : 0;
    var secondaryMatch = has(product.problema, secondary) ? 1 : 0;

    var objectiveSelected = answers.obiettivo.length;
    var overlapValues = answers.obiettivo.filter(function (v) {
      return has(product.obiettivo, v);
    });
    var objectiveTerm = objectiveSelected > 0 ? (2 * overlapValues.length) / Math.sqrt(objectiveSelected) : 0;

    var typeMatch = has(product.tipo_capello, answers.tipo_capello) ? 1 : 0;
    var needMatch = has(product.esigenza, answers.esigenza) ? 1 : 0;
    var porosity = answers.porosita === "yes" && has(product.problema, "porosita") ? 1 : 0;
    var thermal = answers.calore === "yes" && has(product.esigenza, "protezione-termica") ? 1 : 0;
    var curlDefinition =
      isCurlQuestionApplicable() && answers.definizione_ricci === "yes" && has(product.obiettivo, "definizione") ? 1 : 0;

    var raw =
      primaryMatch * 3 +
      secondaryMatch * 1.5 +
      objectiveTerm +
      typeMatch * 1 +
      needMatch * 2 +
      porosity * 1 +
      thermal * 1 +
      curlDefinition * 1;

    // round away floating-point noise so equal scores tie deterministically
    var score = Math.round(raw * 1e6) / 1e6;

    return {
      score: score,
      reasons: {
        primary: primaryMatch ? primary : null,
        secondary: secondaryMatch ? secondary : null,
        objectives: overlapValues,
        type: typeMatch ? answers.tipo_capello : null,
        need: needMatch ? answers.esigenza : null,
        porosity: !!porosity,
        thermal: !!thermal,
        curlDefinition: !!curlDefinition
      }
    };
  }

  function idAsNumber(id) {
    var n = parseInt(id, 10);
    return isNaN(n) ? 0 : n;
  }

  function sortByScoreThenId(list) {
    return list.slice().sort(function (a, b) {
      if (b.score !== a.score) return b.score - a.score;
      return idAsNumber(a.product.id) - idAsNumber(b.product.id);
    });
  }

  /* ---------------------------------------------------------------
   * Routine + fallback construction
   * ------------------------------------------------------------- */

  function mappedStep(product) {
    return Object.prototype.hasOwnProperty.call(ROUTINE_STEP_MAP, product.routine_step)
      ? ROUTINE_STEP_MAP[product.routine_step]
      : null;
  }

  function buildResult() {
    var scored = dataset.map(function (p) {
      var s = scoreProduct(p);
      return { product: p, score: s.score, reasons: s.reasons };
    });

    var routine = {};
    var usedIds = {};
    var totalAssigned = 0;

    REAL_STEPS.forEach(function (step) {
      var candidates = scored.filter(function (s) {
        return mappedStep(s.product) === step && s.score > 0 && !usedIds[s.product.id];
      });
      if (!candidates.length) return;
      var chosen = sortByScoreThenId(candidates)[0];
      routine[step] = chosen;
      usedIds[chosen.product.id] = true;
      totalAssigned += 1;
    });

    if (totalAssigned > 0) {
      return { level: 1, routine: routine, fallback: [] };
    }

    if (answers.esigenza) {
      var fallbackCandidates = scored.filter(function (s) {
        return has(s.product.esigenza, answers.esigenza) && s.score > 0;
      });
      fallbackCandidates = sortByScoreThenId(fallbackCandidates).slice(0, FALLBACK_MAX);
      if (fallbackCandidates.length) {
        return { level: 2, routine: {}, fallback: fallbackCandidates };
      }
    }

    return { level: 3, routine: {}, fallback: [] };
  }

  /* ---------------------------------------------------------------
   * "Why recommended" — built only from the real values that actually
   * matched for this product. Plain text only.
   * ------------------------------------------------------------- */

  function label(value) {
    return String(value).replace(/-/g, " ");
  }

  function joinList(values) {
    var l = values.map(label);
    if (l.length <= 1) return l.join("");
    return l.slice(0, -1).join(", ") + " e " + l[l.length - 1];
  }

  function whyRecommended(reasons) {
    var parts = [];

    if (reasons.primary && reasons.secondary) {
      parts.push("con la criticità principale (" + label(reasons.primary) + ") e secondaria (" + label(reasons.secondary) + ")");
    } else if (reasons.primary) {
      parts.push("con la criticità principale (" + label(reasons.primary) + ")");
    } else if (reasons.secondary) {
      parts.push("con la criticità secondaria (" + label(reasons.secondary) + ")");
    }

    if (reasons.objectives.length === 1) {
      parts.push("con il risultato richiesto (" + label(reasons.objectives[0]) + ")");
    } else if (reasons.objectives.length > 1) {
      parts.push("con i risultati richiesti (" + joinList(reasons.objectives) + ")");
    }

    // Only when neither problems nor objectives matched: state the other
    // real criteria that produced the score (never invented).
    if (!parts.length) {
      if (reasons.need) parts.push("con l'esigenza selezionata (" + label(reasons.need) + ")");
      if (reasons.type) parts.push("con il tipo di capello analizzato (" + label(reasons.type) + ")");
      if (reasons.porosity) parts.push("con l'indicazione di porosità");
      if (reasons.thermal) parts.push("con l'uso di strumenti di calore");
      if (reasons.curlDefinition) parts.push("con l'obiettivo di definizione del riccio");
    }

    if (!parts.length) return "";
    return "Indicato perché coerente " + parts.join(" e ") + ".";
  }

  /* ---------------------------------------------------------------
   * DOM — move/show/hide the pre-rendered Product Card pool only.
   * ------------------------------------------------------------- */

  function resetPool() {
    var items = Array.prototype.slice.call(root.querySelectorAll("[data-aevia-advisor-product-id]"));
    items.forEach(function (item) {
      item.hidden = true;
      poolEl.appendChild(item);
      var note = item.querySelector("[data-aevia-match-note]");
      if (note) note.textContent = "";
    });
  }

  function placeProduct(entry, containerEl) {
    var item = root.querySelector('[data-aevia-advisor-product-id="' + entry.product.id + '"]');
    if (!item) return; // every scored product must already exist in the Liquid-rendered pool
    var note = item.querySelector("[data-aevia-match-note]");
    if (note) note.textContent = whyRecommended(entry.reasons);
    item.hidden = false;
    containerEl.appendChild(item);
  }

  function renderResults(result) {
    resetPool();
    routineStepEls.forEach(function (stepEl) {
      stepEl.hidden = true;
    });
    if (fallbackEl) fallbackEl.hidden = true;
    if (noResultsEl) noResultsEl.hidden = true;

    if (result.level === 1) {
      REAL_STEPS.forEach(function (step) {
        var entry = result.routine[step];
        if (!entry) return;
        var stepEl = root.querySelector('[data-aevia-routine-step="' + step + '"]');
        var productsEl = root.querySelector('[data-aevia-routine-step-products="' + step + '"]');
        if (!stepEl || !productsEl) return;
        placeProduct(entry, productsEl);
        stepEl.hidden = false;
      });
    } else if (result.level === 2 && fallbackEl && fallbackProductsEl) {
      fallbackEl.hidden = false;
      result.fallback.forEach(function (entry) {
        placeProduct(entry, fallbackProductsEl);
      });
    } else if (noResultsEl) {
      noResultsEl.hidden = false;
    }

    resultsEl.hidden = false;
    form.hidden = true;
    resultsEl.focus();
  }

  function submitAdvisor() {
    renderResults(buildResult());
  }

  /* ---------------------------------------------------------------
   * Restart — full in-memory reset, no persistence anywhere.
   * ------------------------------------------------------------- */

  function resetAll() {
    answers = emptyAnswers();
    currentIndex = 0;

    form.reset();
    Array.prototype.slice.call(form.querySelectorAll("[data-aevia-input]")).forEach(function (i) {
      i.disabled = false;
    });
    Array.prototype.slice
      .call(form.querySelectorAll("[data-aevia-cap-message], [data-aevia-nudge], [data-aevia-error]"))
      .forEach(function (m) {
        m.hidden = true;
      });

    resetPool();
    resultsEl.hidden = true;
    form.hidden = false;
    showStep(0);
  }

  if (restartBtn) restartBtn.addEventListener("click", resetAll);

  showStep(0);
})();
